// Coach horizon hook + helpers (Plan 4 U8).
//
// The v2 coach contract is a rolling-window iteration model: each weekly
// call produces a fresh detailed_plan covering ~14 days + an arc beyond.
// Under normal operation Sunday cron extends the window forward. When
// the cron fails / the operator skips a week / the latest call's
// detailed_plan_end_date passes, the operator should see that visibly
// so they can re-run coach to refresh the prescription.
//
// This hook reads GET /api/coach/latest/horizon and exposes a derived
// signal the REVIEW + Day surfaces use to render the countdown badge
// and the past-horizon banner.

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from './api';

const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

export const CoachHorizonSchema = z.object({
  horizon: z
    .object({
      coach_call_id: z.number(),
      date_lisbon: z.string().regex(isoDateRegex),
      detailed_plan_end_date: z.string().regex(isoDateRegex),
      plan_target_date: z.string().regex(isoDateRegex),
      applied_count: z.number(),
      last_called_at: z.number(),
    })
    .nullable(),
});
export type CoachHorizon = z.infer<typeof CoachHorizonSchema>;

export type HorizonStatus = {
  /** Null when no v2 coach_call ok row exists yet. */
  horizon: CoachHorizon['horizon'];
  /**
   * Days remaining until the detailed_plan_end_date (today inclusive).
   * Negative when already past; null when horizon is null.
   */
  daysUntilExpiry: number | null;
  /** True when daysUntilExpiry < 0. */
  expired: boolean;
  /** True when daysUntilExpiry ≤ 2 (operator should re-run soon). */
  expiringSoon: boolean;
};

function daysBetween(fromIso: string, toIso: string): number {
  const [fy, fm, fd] = fromIso.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = toIso.split('-').map(Number) as [number, number, number];
  const from = Date.UTC(fy, fm - 1, fd);
  const to = Date.UTC(ty, tm - 1, td);
  return Math.round((to - from) / 86_400_000);
}

function todayIsoLocal(): string {
  // Compute the operator's local-day in Europe/Lisbon — the server's
  // coach_call.date_lisbon is timezone-anchored there, so client-local
  // would otherwise drift off-by-one when the operator runs at the
  // boundary (e.g., midnight UTC = 01:00 Lisbon in winter, 00:00 in
  // summer). Intl handles DST + locale-formatting consistency.
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Lisbon',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
    const m = parts.find((p) => p.type === 'month')?.value ?? '01';
    const d = parts.find((p) => p.type === 'day')?.value ?? '01';
    return `${y}-${m}-${d}`;
  } catch {
    // Defensive fallback for environments without Intl (none in modern
    // browsers; would mostly affect SSR).
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}

export function deriveHorizonStatus(
  horizon: CoachHorizon['horizon'],
  todayIso: string = todayIsoLocal(),
): HorizonStatus {
  if (!horizon) {
    return { horizon, daysUntilExpiry: null, expired: false, expiringSoon: false };
  }
  const days = daysBetween(todayIso, horizon.detailed_plan_end_date);
  return {
    horizon,
    daysUntilExpiry: days,
    expired: days < 0,
    expiringSoon: days >= 0 && days <= 2,
  };
}

export function useCoachHorizon() {
  return useQuery<CoachHorizon>({
    queryKey: ['coach-horizon'],
    queryFn: () =>
      apiClient.get<CoachHorizon>('/api/coach/latest/horizon', {
        schema: CoachHorizonSchema,
      }),
    retry: 1,
  });
}
